import { useEffect, useRef } from "react";

export interface ContextMenuItem {
  label: string;
  onClick: () => void;
  /** Visually disabled and not clickable. */
  disabled?: boolean;
  /** Renders a danger-colored option (e.g., delete). */
  danger?: boolean;
  /** Right-aligned hint text (e.g., "admin"). */
  hint?: string;
  /** Insert a divider above this item. */
  divider?: boolean;
}

interface ContextMenuProps {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

export function ContextMenu({ x, y, items, onClose }: ContextMenuProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent | TouchEvent) {
      if (!ref.current) return;
      if (e.target instanceof Node && ref.current.contains(e.target)) return;
      onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("mousedown", onDoc);
    window.addEventListener("touchstart", onDoc);
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onClose, true);
    return () => {
      window.removeEventListener("mousedown", onDoc);
      window.removeEventListener("touchstart", onDoc);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onClose, true);
    };
  }, [onClose]);

  // Clamp to viewport so the menu doesn't render off-screen on tiles near edges.
  const W = 220;
  const H = items.length * 32 + 12;
  const cx = Math.min(x, window.innerWidth - W - 8);
  const cy = Math.min(y, window.innerHeight - H - 8);

  return (
    <div
      ref={ref}
      className="fixed z-50 min-w-[220px] rounded border border-zinc-700 bg-zinc-900/95 py-1 text-sm shadow-xl backdrop-blur"
      style={{ left: cx, top: cy }}
      role="menu"
    >
      {items.map((it, idx) => (
        <div key={idx}>
          {it.divider && <div className="my-1 border-t border-zinc-800" />}
          <button
            disabled={it.disabled}
            onClick={(e) => {
              e.preventDefault();
              if (it.disabled) return;
              it.onClick();
              onClose();
            }}
            className={
              "flex w-full items-center justify-between px-3 py-1.5 text-left transition-colors " +
              (it.disabled
                ? "cursor-not-allowed text-zinc-600"
                : it.danger
                  ? "text-rose-300 hover:bg-rose-700/30"
                  : "text-zinc-200 hover:bg-zinc-800")
            }
            role="menuitem"
          >
            <span>{it.label}</span>
            {it.hint && <span className="ml-3 text-[10px] uppercase tracking-wider text-zinc-500">{it.hint}</span>}
          </button>
        </div>
      ))}
    </div>
  );
}
